import QrCodeScannerRounded from '@mui/icons-material/QrCodeScannerRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import {
  Box,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';
import { api, unwrap } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { money, qty } from '../../../lib/format';
import { useShop } from '../../../shop/ShopProvider';
import { dec, toNumber } from '../lib/decimal';
import type { VariantSummary } from '../lib/api';
import { lookupVariant, parseEntry } from '../lib/lookup';
import { ItemTile, Kbd } from './bits';

export interface ProductSearchHandle {
  focus: () => void;
  clear: () => void;
  hasText: () => boolean;
  /** Types text into the search (used when a scanner fires while focus is elsewhere). */
  type: (text: string) => void;
}

function useDebounced<T>(value: T, delay: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

/**
 * The counter's search box. Barcode scanners type the code and press Enter: Enter looks the code up
 * exactly (`/variants/lookup`), otherwise picks the highlighted (or only) search result.
 */
export function ProductSearch({
  onPick,
  onNotFound,
  ref,
}: {
  onPick: (variant: VariantSummary, qty: string) => void;
  onNotFound: (code: string) => void;
  ref?: Ref<ProductSearchHandle>;
}) {
  const { can } = useAuth();
  const { format } = useShop();
  const input = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  const [focused, setFocused] = useState(true);
  const [active, setActive] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [miss, setMiss] = useState(false);
  const entry = parseEntry(text);
  const term = useDebounced(entry.text, 140);

  useImperativeHandle(ref, () => ({
    focus: () => input.current?.focus(),
    clear: () => setText(''),
    hasText: () => text.length > 0,
    type: (t: string) => {
      setText((prev) => prev + t);
      input.current?.focus();
    },
  }));

  const results = useQuery({
    queryKey: ['variants', 'pos-search', term],
    queryFn: () => unwrap(api.GET('/variants', { params: { query: { q: term, pageSize: 8 } } })),
    enabled: term.length > 0,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
  const stock = useQuery({
    queryKey: ['stock', 'pos-search', term],
    queryFn: () =>
      unwrap(api.GET('/stock/balances', { params: { query: { q: term, pageSize: 50 } } })),
    enabled: term.length > 0 && can('inventory.view'),
    staleTime: 30_000,
  });
  const available = new Map<string, bigint>();
  for (const b of stock.data?.data ?? [])
    available.set(b.variantId, (available.get(b.variantId) ?? 0n) + dec(b.available));

  const rows = entry.text.length > 0 ? (results.data?.data ?? []) : [];
  const open = focused && entry.text.length > 0 && (rows.length > 0 || !results.isFetching);

  const pick = (v: VariantSummary) => {
    onPick(v, entry.qty);
    setText('');
    setActive(-1);
    setMiss(false);
  };

  const submit = async () => {
    if (!entry.text) return;
    if (active >= 0 && rows[active]) {
      pick(rows[active]);
      return;
    }
    setBusy(true);
    try {
      const v = await lookupVariant(entry.text);
      const fresh = term === entry.text ? rows : [];
      if (v) pick(v);
      else if (fresh.length === 1 && fresh[0]) pick(fresh[0]);
      else {
        setMiss(true);
        input.current?.select();
        onNotFound(entry.text);
      }
    } catch (e) {
      setMiss(true);
      onNotFound(e instanceof Error ? e.message : entry.text);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <TextField
        inputRef={input}
        value={text}
        autoFocus
        placeholder="Scan a barcode or search items…"
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          setText(e.target.value);
          setActive(-1);
          setMiss(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && rows.length) {
            e.preventDefault();
            setActive((i) => (i + 1) % rows.length);
          } else if (e.key === 'ArrowUp' && rows.length) {
            e.preventDefault();
            setActive((i) => (i <= 0 ? rows.length - 1 : i - 1));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            void submit();
          } else if (e.key === 'Escape' && text) {
            e.preventDefault();
            e.stopPropagation();
            setText('');
            setActive(-1);
            setMiss(false);
          }
        }}
        error={miss}
        slotProps={{
          htmlInput: {
            'aria-label': 'Scan or search items',
            autoComplete: 'off',
            spellCheck: false,
            role: 'combobox',
            'aria-expanded': open,
            'aria-controls': 'pos-search-results',
          },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                {busy ? (
                  <CircularProgress size={20} />
                ) : (
                  <SearchRounded sx={{ color: miss ? 'error.main' : 'text.secondary' }} />
                )}
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <QrCodeScannerRounded
                    sx={{ color: 'text.disabled', display: { xs: 'none', sm: 'block' } }}
                  />
                  <Kbd>F2</Kbd>
                </Stack>
              </InputAdornment>
            ),
            sx: {
              height: 56,
              fontSize: '1.0625rem',
              borderRadius: '14px',
              boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)',
              animation: miss ? 'sm-shake 260ms' : undefined,
              '@keyframes sm-shake': {
                '25%': { transform: 'translateX(-4px)' },
                '75%': { transform: 'translateX(4px)' },
              },
            },
          },
        }}
      />
      {miss && (
        <Typography
          variant="caption"
          color="error"
          sx={{ position: 'absolute', left: 16, top: 60 }}
        >
          No item with the code “{entry.text}”. Try a name or SKU.
        </Typography>
      )}
      {open && !miss && (
        <Paper
          id="pos-search-results"
          role="listbox"
          elevation={0}
          sx={(t) => ({
            position: 'absolute',
            zIndex: t.zIndex.modal - 1,
            top: 62,
            left: 0,
            right: 0,
            p: 0.75,
            border: 1,
            borderColor: 'divider',
            borderRadius: '14px',
            boxShadow: '0 24px 48px -12px rgba(16,24,40,0.22), 0 8px 16px -4px rgba(16,24,40,0.08)',
            maxHeight: 440,
            overflowY: 'auto',
          })}
        >
          {rows.length === 0 ? (
            <Box sx={{ p: 2.5, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No items match “{entry.text}”. Press Enter to look up the exact code.
              </Typography>
            </Box>
          ) : (
            rows.map((v, i) => {
              const avail = available.get(v.id);
              return (
                <Box
                  key={v.id}
                  role="option"
                  aria-selected={i === active}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(v);
                  }}
                  onMouseEnter={() => setActive(i)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.25,
                    py: 1,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    bgcolor: i === active ? 'action.selected' : 'transparent',
                  }}
                >
                  <ItemTile name={v.displayName} size={40} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600 }} noWrap>
                      {v.displayName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      {[v.sku, v.barcode].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                  {avail !== undefined && v.trackInventory && (
                    <Typography
                      variant="caption"
                      className="tabular"
                      sx={{
                        fontWeight: 600,
                        color: avail > 0n ? 'success.main' : 'error.main',
                        display: { xs: 'none', sm: 'block' },
                      }}
                    >
                      {avail > 0n
                        ? `${qty(toNumber(avail), format)} ${v.baseUnit.symbol} in stock`
                        : 'Out of stock'}
                    </Typography>
                  )}
                  <Typography
                    className="tabular"
                    sx={{ fontWeight: 650, minWidth: 90, textAlign: 'right' }}
                  >
                    {money(v.salePrice, format)}
                  </Typography>
                </Box>
              );
            })
          )}
        </Paper>
      )}
    </Box>
  );
}
